<script setup>
/**
 * @typedef {Object} FeedbackImageItem
 * @property {string} id - 图片唯一标识
 * @property {File} file - 原始文件对象
 * @property {string} previewUrl - 预览地址
 * @property {string} [filePath] - 上传后的文件路径
 */

/**
 * @typedef {Object} FeedbackPayload
 * @property {string} type - 问题类型
 * @property {string} content - 反馈描述
 * @property {Array<string>} images - 图片路径列表
 */

/**
 * @description 意见反馈弹窗内容表单
 * @param {number} maxLength - 描述最大字数
 * @param {number} maxImages - 最大图片数量
 * @param {string} selectPopupContainer - 问题类型下拉浮层挂载容器
 * @emits submitted - 提交成功后触发
 */

import {
  Button as AButton,
  Option as AOption,
  Select as ASelect,
  Textarea as ATextarea,
} from '@arco-design/web-vue'
import { IconClose, IconInfoCircleFill, IconPlus } from '@arco-design/web-vue/es/icon'
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { BaseMessage } from '~/utils/baseMessage'
import { baseApi } from '~/api/base'
import { websiteApi } from '~/api/website'
import { useDebounceFn } from '@/composables/useDebounceThrottle'
import { useFileUpload } from '~/composables/useFileUpload'

const props = defineProps({
  maxLength: {
    type: Number,
    default: 500,
  },
  maxImages: {
    type: Number,
    default: 5,
  },
  showActions: {
    type: Boolean,
    default: true,
  },
  selectPopupContainer: {
    type: String,
    default: 'body',
  },
})

const emit = defineEmits(['submitted'])

const formState = reactive({
  type: '',
  description: '',
})

const MAX_IMAGE_SIZE = 5 * 1024 * 1024
const IMAGE_SIZE_LIMIT_MESSAGE = '图片大小上限为5M'
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg']

const reasonList = ref([])
const hasLoaded = ref(false)
const isLoadingReasons = ref(false)

const imageList = ref(/** @type {Array<FeedbackImageItem>} */ ([]))
const isSubmitting = ref(false)

const { uploadImage } = useFileUpload()

const reasonSelectTriggerProps = {
  autoFitPopupWidth: true,
  contentClass: 'feedback-modal__select-dropdown',
  contentStyle: { zIndex: 1400 },
  updateAtScroll: true,
}

const remainingCount = computed(() => {
  const count = props.maxLength - formState.description.length
  return count >= 0 ? count : 0
})

const canAddImage = computed(() => {
  return imageList.value.length < props.maxImages
})

const isFormValid = computed(() => {
  return formState.type && formState.description.trim()
})

/**
 * @description 触发问题类型枚举加载
 * @returns {void}
 */
const ensureReasonList = () => {
  if (hasLoaded.value || isLoadingReasons.value) return
  getReasonList()
}

/**
 * @description 弹层展开时确保枚举已加载
 * @param {boolean} visible - 弹层是否可见
 * @returns {void}
 */
const handleReasonPopupVisibleChange = (visible) => {
  if (!visible) return
  ensureReasonList()
}

const getReasonList = async () => {
  if (hasLoaded.value || isLoadingReasons.value) return

  isLoadingReasons.value = true

  try {
    const data = await baseApi.getTypeValueList('13')
    const list = data?.[13] || []
    reasonList.value = list
    hasLoaded.value = true
    if (!formState.type) {
      formState.type = ''
    }
  } catch (error) {
    console.error('获取反馈类型列表失败:', error)
    // BaseMessage.error('获取反馈类型失败') // axios 已统一提示
  } finally {
    isLoadingReasons.value = false
  }
}

const validateImageFile = (file) => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return '请选择图片文件（jpg、jpeg、png）'
  }

  if (file.size > MAX_IMAGE_SIZE) {
    return IMAGE_SIZE_LIMIT_MESSAGE
  }

  return ''
}

const handleFileChange = (event) => {
  const files = Array.from(event.target?.files || [])
  if (!files.length) {
    event.target.value = ''
    return
  }

  const validFiles = []

  for (const file of files) {
    const errorMessage = validateImageFile(file)
    if (errorMessage) {
      BaseMessage.error(errorMessage)
      continue
    }

    validFiles.push(file)
  }

  if (!validFiles.length) {
    event.target.value = ''
    return
  }

  const remain = props.maxImages - imageList.value.length
  const selectedFiles = validFiles.slice(0, remain)

  selectedFiles.forEach((file) => {
    const previewUrl = URL.createObjectURL(file)
    imageList.value.push({
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      file,
      previewUrl,
    })
  })

  event.target.value = ''
}

const handleRemoveImage = (id) => {
  const targetIndex = imageList.value.findIndex((item) => item.id === id)
  if (targetIndex === -1) return

  const [removed] = imageList.value.splice(targetIndex, 1)
  if (removed?.previewUrl) {
    URL.revokeObjectURL(removed.previewUrl)
  }
}

const doSubmit = async () => {
  if (!formState.type) {
    BaseMessage.error('请选择问题类型')
    return
  }
  if (!formState.description.trim()) {
    BaseMessage.error('请填写反馈描述')
    return
  }

  const invalidImage = imageList.value.find((item) => validateImageFile(item.file))
  if (invalidImage) {
    BaseMessage.error(validateImageFile(invalidImage.file))
    return
  }

  isSubmitting.value = true

  try {
    const imagePaths = []

    for (const item of imageList.value) {
      if (!item.filePath) {
        const filePath = await uploadImage(item.file, {
          allowedTypes: ALLOWED_IMAGE_TYPES,
          maxSize: MAX_IMAGE_SIZE,
        })
        item.filePath = filePath
      }
      imagePaths.push(item.filePath)
    }

    /** @type {FeedbackPayload} */
    const payload = {
      type: formState.type,
      content: formState.description.trim(),
      images: imagePaths,
    }

    await websiteApi.submitFeedback(payload)

    BaseMessage.success('感谢您的反馈，我们将尽快处理您提到的问题')
    emit('submitted')
  } catch (error) {
    if (error?.message) {
      BaseMessage.error(error.message)
    }
  } finally {
    isSubmitting.value = false
  }
}
const handleSubmit = useDebounceFn(doSubmit, 500, {
  leading: true,
  trailing: false,
})

onUnmounted(() => {
  imageList.value.forEach((item) => {
    if (item.previewUrl) {
      URL.revokeObjectURL(item.previewUrl)
    }
  })
})

onMounted(() => {
  ensureReasonList()
})

defineExpose({
  submit: handleSubmit,
  isSubmitting,
  isFormValid,
})
</script>

<template>
  <div class="feedback-modal" :class="{ 'feedback-modal--with-actions': showActions }">
    <div class="feedback-modal__notice">
      <IconInfoCircleFill class="feedback-modal__notice-icon" />
      <span>订单、售后问题请直接咨询客服，可高效为您处理</span>
    </div>

    <div class="feedback-modal__field">
      <div class="feedback-modal__label">问题类型</div>
      <ASelect
        v-if="reasonList?.length"
        v-model="formState.type"
        placeholder="请选择"
        :loading="isLoadingReasons"
        :popup-container="selectPopupContainer"
        :trigger-props="reasonSelectTriggerProps"
        @popup-visible-change="handleReasonPopupVisibleChange"
      >
        <AOption v-for="item in reasonList" :key="item" :value="item">
          {{ item }}
        </AOption>
      </ASelect>
      <div v-else class="feedback-modal__select-placeholder" @click="ensureReasonList">
        {{ isLoadingReasons ? '加载中...' : '暂无可选项' }}
      </div>
    </div>

    <div class="feedback-modal__field">
      <div class="feedback-modal__label">反馈描述</div>
      <div class="feedback-modal__textarea">
        <ATextarea
          v-model="formState.description"
          :max-length="maxLength"
          placeholder="请详细描述遇到的问题"
        />
        <div class="feedback-modal__count">{{ remainingCount }}/{{ maxLength }}</div>
      </div>
    </div>

    <div class="feedback-modal__field">
      <div class="feedback-modal__label">
        上传图片
        <span class="feedback-modal__label--optional">(选填)</span>
      </div>
      <div class="feedback-modal__upload">
        <div class="feedback-modal__upload-grid">
          <div v-for="item in imageList" :key="item.id" class="feedback-modal__upload-item">
            <img :src="item.previewUrl" alt="反馈图片" />
            <button
              class="feedback-modal__remove"
              type="button"
              aria-label="删除图片"
              @click="handleRemoveImage(item.id)"
            >
              <IconClose />
            </button>
          </div>

          <label v-if="canAddImage" class="feedback-modal__upload-card">
            <input
              class="feedback-modal__file-input"
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              multiple
              @change="handleFileChange"
            />
            <div class="feedback-modal__upload-plus">
              <IconPlus />
            </div>
            <div class="feedback-modal__upload-text">上传</div>
          </label>
        </div>
        <div class="feedback-modal__upload-tip">
          1-{{ maxImages }}张，支持jpg、jpeg、png，单张大小上限为5MB
        </div>
      </div>
    </div>

    <div v-if="showActions" class="feedback-modal__actions">
      <AButton
        type="primary"
        :loading="isSubmitting"
        :disabled="!isFormValid"
        @click="handleSubmit"
      >
        提交
      </AButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.feedback-modal {
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: $text-title-color;
}

.feedback-modal--with-actions {
  min-height: 100%;
}

.feedback-modal__notice {
  display: flex;
  align-items: center;
  gap: $spacing-xs;
  padding: 9px $spacing-md;
  border-radius: 6px;
  background-color: $auxiliary-color-4;
  color: $text-strong-color;
  font-size: $font-size-card-content;
  line-height: $line-height-md;
}

.feedback-modal__notice-icon {
  font-size: $font-size-card-subtitle;
  color: $tertiary-color;
}

.feedback-modal__field {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.feedback-modal__label {
  font-size: $font-size-card-content;
  color: $text-title-color;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: $spacing-2xs;
}

.feedback-modal__label--optional {
  font-size: $font-size-card-content;
  color: $text-subtitle-color;
  font-weight: 400;
}

.feedback-modal__textarea {
  position: relative;
}

.feedback-modal__select-placeholder {
  height: 40px;
  border-radius: 6px;
  border: 1px solid transparent;
  background-color: $bg-color;
  display: flex;
  align-items: center;
  padding: 0 $spacing-md;
  font-size: $font-size-card-content;
  color: $text-subtitle-color;
  cursor: not-allowed;
}

.feedback-modal__count {
  position: absolute;
  right: $spacing-sm;
  bottom: $spacing-sm;
  font-size: $font-size-caption;
  color: $text-stat-zero-color;
}

.feedback-modal__upload {
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;
}

.feedback-modal__upload-grid {
  display: grid;
  grid-template-columns: repeat(3, 96px);
  gap: 10px;
}

.feedback-modal__upload-item {
  width: 96px;
  height: 96px;
  border-radius: 2px;
  border: 1px solid transparent;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.feedback-modal__remove {
  position: absolute;
  top: $spacing-2xs;
  right: $spacing-2xs;
  width: $spacing-lg;
  height: $spacing-lg;
  border-radius: 50%;
  border: none;
  background-color: rgba($color-black, 0.5);
  color: $bg-white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.feedback-modal__upload-card {
  width: 96px;
  height: 96px;
  border-radius: 2px;
  border: 1px dashed $bg-color;
  background-color: $bg-light-gray;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: $text-subtitle-color;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: $bg-color;
  }
}

.feedback-modal__upload-plus {
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feedback-modal__upload-text {
  font-size: $font-size-card-content;
  font-weight: 500;
}

.feedback-modal__upload-tip {
  font-size: $font-size-caption;
  color: $text-subtitle-color;
}

.feedback-modal__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
}

.feedback-modal__file-input {
  display: none;
}

.feedback-modal {
  :deep(.arco-select-view) {
    height: 40px;
    border-radius: 6px;
    border-color: transparent;
    background-color: $bg-color;
    box-shadow: none;
    padding: 0 16px;
  }

  :deep(.arco-select-view-input) {
    font-size: $font-size-card-content;
    color: $text-title-color;
  }

  :deep(.arco-select-placeholder) {
    font-size: $font-size-card-content;
    color: $text-subtitle-color;
  }

  :deep(.arco-textarea-wrapper) {
    border-radius: 6px;
    border-color: transparent;
    background-color: $bg-color;
    padding: 0;
  }

  :deep(.arco-textarea) {
    border-radius: 6px;
    border-color: transparent;
    background-color: transparent;
    min-height: 102px;
    padding: 9px 12px;
  }

  :deep(.arco-textarea textarea) {
    font-size: $font-size-card-content;
    color: $text-title-color;
    line-height: $line-height-md;
    padding: 0;
  }

  :deep(.arco-btn-primary) {
    height: 40px;
    padding: 0 $spacing-md;
    border-radius: 6px;
    background-color: $primary-color;
    font-size: $font-size-card-content;
    font-weight: 500;
  }
}

:global(.feedback-modal__select-dropdown) {
  z-index: 1400 !important;

  .arco-select-dropdown {
    box-sizing: border-box;
    border: 1px solid $border-light-color;
    border-radius: 6px;
    box-shadow: $shadow-dropdown-level1;
  }

  .arco-select-option {
    min-height: 36px;
    font-size: $font-size-card-content;
    color: $text-title-color;
  }
}

@include respond-to-max('md') {
  .feedback-modal {
    gap: $spacing-md;
  }

  .feedback-modal__field {
    gap: $spacing-xs;
  }

  .feedback-modal__upload-grid {
    grid-template-columns: repeat(auto-fill, 96px);
    gap: $spacing-xs;
    justify-content: flex-start;
  }

  .feedback-modal__upload-item,
  .feedback-modal__upload-card {
    width: 96px;
    // H5 下固定上传宫格尺寸，避免随列宽拉伸
    height: 96px;
    border-radius: 6px;
  }

  .feedback-modal__actions {
    position: sticky;
    bottom: 0;
    z-index: 1;
    margin-top: auto;
    padding-top: $spacing-sm;
    padding-bottom: calc($spacing-sm + env(safe-area-inset-bottom));
    background-color: $bg-white;

    .arco-btn {
      width: 100%;
    }
  }

  .feedback-modal__notice {
    padding: $spacing-xs $spacing-sm;
  }
}
</style>
